import React, { useCallback, useState, useEffect, useMemo } from "react";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import {
  getTimePeriodDates,
  resolveTimePeriodInWindow,
} from "@utils/time-period-utils";
import { getTimePeriodTitleSuffix } from "@utils/time-period-display";
import { getRollingAverageTitleSuffix } from "@utils/rolling-average-utils";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  TextField,
  FormHelperText,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WarningIcon from "@mui/icons-material/Warning";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  tableConfigSelector,
  tableConfigDispatcher,
  StatTestType,
  SignificanceLevel,
  TimePeriod,
} from "@store/slices/table-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { questionSessionMemory } from "@/utils/question-session-memory";
import {
  useGetDatasetQuestionsResponseMutation,
  useGetDatasetsQuery,
  useGetDatasetTimeBoundsQuery,
  useGetWeightConfigurationsQuery,
  useGetDatasetByIdQuery,
} from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type PointInTimeUnit,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
} from "@models/Dataset";
import { updateViewerOptions } from "@store/slices/table-config-slice";
import PointInTimeViewerOptionsSection from "./PointInTimeViewerOptionsSection";
import TableChart from "./TableChart";
import TimePeriodSelection from "./TimePeriodSelection";
import RollingAverageSection from "./RollingAverageSection";
import HPInput from "../common/Input";
import HPButton from "../common/Button";
import DrawerFilterToggles from "./common/DrawerFilterToggles";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyChartState from "../common/EmptyChartState";
import { useDebounce } from "@hooks/use-debounce";

const VIEWER_PIT_PERIODS = new Set([
  "latest_quarter",
  "previous_quarter",
  "latest_semester",
  "previous_semester",
]);

// Sortable Row Item Component
interface SortableRowItemProps {
  row: {
    variableId: string;
    variableLabel: string;
  };
  isHidden: boolean;
  hasCustomLabel: boolean;
  displayLabel: string;
  isEditing: boolean;
  editValue: string;
  onToggleVisibility: (variableId: string) => void;
  onStartEdit: (row: { variableId: string; variableLabel: string }) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onResetLabel: (variableId: string) => void;
  onEditValueChange: (value: string) => void;
  isDisabled: boolean;
  isDragDisabled: boolean;
}

const SortableRowItem: React.FC<SortableRowItemProps> = ({
  row,
  isHidden,
  hasCustomLabel,
  displayLabel,
  isEditing,
  editValue,
  onToggleVisibility,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onResetLabel,
  onEditValueChange,
  isDisabled,
  isDragDisabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.variableId, disabled: isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag handle */}
      {!isDragDisabled && !isDisabled && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Visibility toggle */}
      <IconButton
        size="small"
        onClick={() => onToggleVisibility(row.variableId)}
        disabled={isDisabled}
        sx={{ flexShrink: 0 }}
      >
        {isHidden ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>

      {/* Label or edit field */}
      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: hasCustomLabel ? 600 : 400,
            }}
          >
            {displayLabel}
          </Typography>
          {hasCustomLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              Original: {row.variableLabel}
            </Typography>
          )}
        </Box>
      )}

      {/* Action buttons */}
      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={() => onStartEdit(row)}
            disabled={isHidden || isDisabled}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {hasCustomLabel && (
            <HPButton
              size="small"
              variant="text"
              onClick={() => onResetLabel(row.variableId)}
              disabled={isDisabled}
            >
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

const TableChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    currentConfig,
    datasetId,
    datasetName,
    questionId,
    datasetVersionId,
    activeFilters,
    applyFilters,
  } = useAppSelector(tableConfigSelector);
  const { isDrawerOpen, editingWidgetId, pendingMemoryEntry } =
    useAppSelector(chartDrawerSelector);
  const questions = useAppSelector((state) => state.tableConfig.question);
  const { data: datasets = [], isFetching: datasetsFetching } =
    useGetDatasetsQuery({});
  const { data: timeBounds, isFetching: timeBoundsFetching } =
    useGetDatasetTimeBoundsQuery(datasetId as string, { skip: !datasetId });
  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });
  const pitGuardrails = React.useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = React.useMemo((): PointInTimeUnit | null => {
    const tp = currentConfig.timePeriodSelection?.timePeriod;
    if (tp === "latest_quarter" || tp === "previous_quarter") return "quarter";
    if (tp === "latest_semester" || tp === "previous_semester")
      return "semester";
    return null;
  }, [currentConfig.timePeriodSelection?.timePeriod]);

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  // "Apply Rolling Average" checkbox is forced OFF (disabled) only when the
  // dataset allows ZERO smoothed window sizes for this PIT unit (i.e. only 0
  // is allowed). When the default is 0 but other periods are also allowed,
  // the researcher must still be able to opt into smoothing manually.
  const isRollingAverageDisabledByPolicy: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  // "Apply Rolling Average" checkbox is forced ON (No-RA disabled) when 0 is
  // not in the allowed list (dataset requires smoothing for this unit).
  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const currentPitPeriod = VIEWER_PIT_PERIODS.has(
    currentConfig.timePeriodSelection?.timePeriod ?? "",
  )
    ? (currentConfig.timePeriodSelection
        ?.timePeriod as ViewerSelectableTimePeriod)
    : null;
  const isViewerOptionsInvalid =
    currentPitPeriod !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;
  const dataAvailabilityCutoff =
    datasets.find((d) => d.id === datasetId)?.dataAvailabilityDate ||
    timeBounds?.maxDate;
  const cutoffSettled = !datasetsFetching && !timeBoundsFetching;

  // Extract timePeriodSelection and weight to ensure React tracks changes
  const timePeriodSelection = currentConfig.timePeriodSelection;
  const weight = currentConfig.weight;
  const [weightsApplied, setWeightsApplied] = React.useState<boolean>(true);
  const isCreatingChart = editingWidgetId === "new-widget";

  // Check if current time period is "all_time" - statistical testing should be disabled
  const isAllTimePeriod =
    currentConfig.timePeriodSelection?.timePeriod === "all_time";

  // Ref to track if we're in the initial load to prevent double fetching
  const isInitialLoad = React.useRef(false);
  // Ref to prevent double fetch on drawer open (React StrictMode or effect re-runs)
  const hasFetchedOnOpen = React.useRef(false);
  // Ref to prevent multiple simultaneous stat testing requests
  const isHandlingStatTestingChange = React.useRef(false);
  // Ref to always have latest rolling average config without adding to useCallback deps
  const rollingAverageRef = React.useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;

  const [getDatasetQuestionsResponse, { isLoading: isRefetchingData }] =
    useGetDatasetQuestionsResponseMutation();

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updatePercentageFormatting,
    updateWeight,
    updateDataSorting,
    updateRowHeaderLabel,
    updateColumnHeaderLabel,
    toggleRowVisibility,
    showAllRows,
    hideAllRows,
    updateTimePeriod,
    updateTimePeriodDates,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateCompareToTotal,
    toggleApplyFilters,
    updateTableData,
    saveCurrentConfig,
    resetToDefault,
    reorderRows,
    resetRowOrder,
    updateCustomRowLabel,
    resetCustomRowLabel,
  } = tableConfigDispatcher(dispatch);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // When creating a new chart, default the weight to DATASET_DEFAULT if the
  // selected dataset has at least one weight configuration.
  useEffect(() => {
    if (
      isCreatingChart &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingChart, weightConfigData]);

  // State for row editing
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editRowValue, setEditRowValue] = useState("");

  // Handle row edit
  const handleStartEditRow = useCallback(
    (row: { variableId: string; variableLabel: string }) => {
      setEditingRowId(row.variableId);
      setEditRowValue(
        currentConfig.customRowLabels[row.variableId] || row.variableLabel,
      );
    },
    [currentConfig.customRowLabels],
  );

  const handleSaveRowEdit = useCallback(() => {
    if (editingRowId && editRowValue.trim()) {
      updateCustomRowLabel({ id: editingRowId, label: editRowValue.trim() });
    }
    setEditingRowId(null);
    setEditRowValue("");
  }, [editingRowId, editRowValue, updateCustomRowLabel]);

  const handleCancelRowEdit = useCallback(() => {
    setEditingRowId(null);
    setEditRowValue("");
  }, []);

  const handleResetRowLabel = useCallback(
    (id: string) => {
      resetCustomRowLabel(id);
      if (editingRowId === id) {
        setEditingRowId(null);
        setEditRowValue("");
      }
    },
    [editingRowId, resetCustomRowLabel],
  );

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end event
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = currentConfig.rowOrder.indexOf(active.id as string);
        const newIndex = currentConfig.rowOrder.indexOf(over.id as string);

        if (oldIndex !== -1 && newIndex !== -1) {
          reorderRows({ fromIndex: oldIndex, toIndex: newIndex });
        }
      }
    },
    [currentConfig.rowOrder, reorderRows],
  );

  // Local state for immediate UI feedback
  const [localApplyFilters, setLocalApplyFilters] = useState(
    activeFilters.length > 0 ? true : false,
  );
  const [isTogglingFilters, setIsTogglingFilters] = useState(false);

  // Toggle for using dashboard date filters in preview
  const [usePreviewDateFilter, setUsePreviewDateFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    return Boolean(dfStart && dfEnd);
  });

  const dashboardDateOverride = useMemo(() => {
    if (!usePreviewDateFilter) return undefined;
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    if (dfStart && dfEnd) return { startDate: dfStart, endDate: dfEnd };
    return undefined;
  }, [usePreviewDateFilter]);

  const isNoPeriodAvailable = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate)
      return false;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod as
      | TimePeriod
      | undefined;
    if (
      !timePeriod ||
      timePeriod === "all_time" ||
      timePeriod === "custom_period"
    )
      return false;
    return !resolveTimePeriodInWindow(
      timePeriod,
      dashboardDateOverride.startDate,
      dashboardDateOverride.endDate,
    );
  }, [dashboardDateOverride, currentConfig.timePeriodSelection?.timePeriod]);

  const noPeriodMessage = useMemo(() => {
    if (!isNoPeriodAvailable) return null;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod ?? "";
    const unit = timePeriod.includes("semester") ? "semester" : "quarter";
    return `No complete ${unit} available within the selected date range.`;
  }, [isNoPeriodAvailable, currentConfig.timePeriodSelection?.timePeriod]);

  // Debounce the filter toggle to avoid too many API calls
  const debouncedApplyFilters = useDebounce(localApplyFilters, 500);

  // Sync local state with redux state on mount and when filters change
  useEffect(() => {
    if (activeFilters.length > 0) {
      setLocalApplyFilters(true);
    } else {
      setLocalApplyFilters(false);
    }
  }, [activeFilters.length]);

  // Fetch data with current filter settings
  const fetchChartData = useCallback(
    async (
      weight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT",
      shouldApplyFilters: boolean,
      overrideDates?: { startDate: string | null; endDate: string | null },
      overridePeriodCount?: number | null,
    ) => {
      if (!datasetId || !questionId) return;

      // Use override dates if provided, otherwise get from Redux state
      // Note: use explicit undefined check, not ??, because null means "no dates" (all_time)
      let startDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.startDate
          : currentConfig.timePeriodSelection?.startDate;
      let endDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.endDate
          : currentConfig.timePeriodSelection?.endDate;

      // Determine if dates should be excluded:
      // - If overrideDates explicitly passes null dates → no dates (all_time signal)
      // - If no overrideDates and Redux timePeriod is all_time → no dates
      // - If overrideDates has actual dates → always include them (even if Redux is stale)
      const explicitNoDates =
        overrideDates !== undefined &&
        !overrideDates.startDate &&
        !overrideDates.endDate;
      const reduxIsAllTime =
        currentConfig.timePeriodSelection?.timePeriod === "all_time";
      const skipDates =
        explicitNoDates || (overrideDates === undefined && reduxIsAllTime);
      if (!skipDates && !startDate && !endDate) {
        // Fallback to responsesMinDate/responsesMaxDate if no dates provided
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          startDate = currentConfig.responsesMinDate;
          endDate = currentConfig.responsesMaxDate;
        }
      }

      const shouldIncludeDates = !skipDates && !!(startDate && endDate);

      try {
        const result = await getDatasetQuestionsResponse({
          id: questionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: weight,
          chartType: "TABLE",
          filters:
            shouldApplyFilters && activeFilters.length > 0
              ? activeFilters
              : undefined,
          // Include date range if both dates are set (as strings, not null)
          ...(shouldIncludeDates && {
            startDate: startDate!,
            endDate: endDate!,
          }),
          // Include statistical testing parameters if enabled AND dates are included (not all_time)
          ...(currentConfig.statisticalTesting?.enabled &&
            shouldIncludeDates && {
              enableStatTesting: true,
              statTestingType: testTypeToApiFormat(
                currentConfig.statisticalTesting?.testType,
              ),
              pValue: significanceLevelToPValue(
                currentConfig.statisticalTesting?.significanceLevel || "90%",
              ),
              minimumSampleSize:
                currentConfig.statisticalTesting?.minimumSampleSize || 30,
            }),
          // Include rolling average periodCount when enabled (not for all_time)
          // Use override if provided (null means explicitly disabled), otherwise read from ref
          ...(() => {
            if (skipDates) return {};
            const ra = rollingAverageRef.current;
            const pc =
              overridePeriodCount !== undefined
                ? overridePeriodCount
                : ra?.enabled
                  ? ra.windowSize
                  : null;
            return pc ? { periodCount: pc } : {};
          })(),
        });

        if (result.data?.data && Array.isArray(result.data.data)) {
          updateTableData({
            data: result.data.data,
            responsesMinDate: result.data.responsesMinDate,
            responsesMaxDate: result.data.responsesMaxDate,
            effectiveRollingPeriodCount:
              result.data?.effectiveRollingPeriodCount,
            rollingPeriodsWarningMessage:
              result.data?.rollingPeriodsWarningMessage,
            comparisonRollingLabel: result.data?.comparisonRollingLabel,
            missingRollingPeriodLabels: result.data?.missingRollingPeriodLabels,
          });

          // Update weightsApplied state from API response
          if (result.data?.weightsApplied !== undefined) {
            setWeightsApplied(result.data.weightsApplied);
          }
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    },
    [
      datasetId,
      questionId,
      activeFilters,
      getDatasetQuestionsResponse,
      updateTableData,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.statisticalTesting,
    ],
  );

  // Fetch data when drawer opens for editing (not creating).
  // For named time periods, wait for availability cutoff queries to settle
  // so we fetch with the correct dates from the start.
  useEffect(() => {
    if (isDrawerOpen && !isCreatingChart && datasetId && questionId) {
      if (hasFetchedOnOpen.current) return;

      const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
      const isNamedPeriod =
        timePeriod &&
        timePeriod !== "all_time" &&
        timePeriod !== "custom_period";

      // Wait for cutoff queries to settle before fetching named periods
      if (isNamedPeriod && !cutoffSettled) return;

      hasFetchedOnOpen.current = true;
      isInitialLoad.current = true;

      const shouldApply = activeFilters.length > 0 && localApplyFilters;

      // Resolve dates using cutoff for named periods
      let overrideDates:
        | { startDate: string | null; endDate: string | null }
        | undefined;
      if (isNamedPeriod && timePeriod) {
        const resolved = getTimePeriodDates(timePeriod, dataAvailabilityCutoff);
        if (resolved) {
          overrideDates = {
            startDate: resolved.startDate,
            endDate: resolved.endDate,
          };
          updateTimePeriodDates(resolved);
        }
      }

      fetchChartData(currentConfig.weight, shouldApply, overrideDates);
    } else if (!isDrawerOpen) {
      isInitialLoad.current = false;
      hasFetchedOnOpen.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isCreatingChart, cutoffSettled]);

  // Effect to refetch data when debounced filter toggle changes
  useEffect(() => {
    if (datasetId && questionId && isTogglingFilters) {
      const refetch = async () => {
        await fetchChartData(currentConfig.weight, debouncedApplyFilters);
        setIsTogglingFilters(false);
      };
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApplyFilters]);

  // Handle filter toggle with immediate UI update
  const handleFilterToggle = useCallback(() => {
    const newValue = !localApplyFilters;
    setLocalApplyFilters(newValue);
    setIsTogglingFilters(true);
    toggleApplyFilters();
  }, [localApplyFilters, toggleApplyFilters]);

  const handlePreviewDateFilterToggle = async (enabled: boolean) => {
    setUsePreviewDateFilter(enabled);

    if (datasetId && questionId) {
      let overrideDates:
        | { startDate: string | null; endDate: string | null }
        | undefined;

      if (enabled) {
        const params = new URLSearchParams(window.location.search);
        const dfStart = params.get("df_start");
        const dfEnd = params.get("df_end");

        if (dfStart && dfEnd) {
          const timePeriod = currentConfig.timePeriodSelection?.timePeriod as
            | TimePeriod
            | undefined;
          const isNamedPeriod =
            timePeriod &&
            timePeriod !== "all_time" &&
            timePeriod !== "custom_period";

          if (isNamedPeriod) {
            const effectiveEnd =
              dataAvailabilityCutoff && dataAvailabilityCutoff < dfEnd
                ? dataAvailabilityCutoff
                : dfEnd;
            const resolved = resolveTimePeriodInWindow(
              timePeriod,
              dfStart,
              effectiveEnd,
              timeBounds?.minDate,
            );
            overrideDates = resolved
              ? { startDate: dfStart, endDate: effectiveEnd }
              : { startDate: null, endDate: null };
          } else {
            overrideDates = { startDate: dfStart, endDate: dfEnd };
          }
        }
      }

      await fetchChartData(
        currentConfig.weight,
        localApplyFilters,
        overrideDates,
      );
    }
  };

  // Auto-disable Statistical Testing when switching to "all_time" period
  useEffect(() => {
    if (isAllTimePeriod && currentConfig.statisticalTesting?.enabled) {
      // Just disable the toggle without triggering refetch
      updateStatTestingEnabled(false);
    }
  }, [
    isAllTimePeriod,
    updateStatTestingEnabled,
    currentConfig.statisticalTesting?.enabled,
  ]);

  const handleWeightChange = useCallback(
    async (newWeight: "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT") => {
      updateWeight(newWeight);

      // Fetch new data with current filter settings
      await fetchChartData(newWeight, debouncedApplyFilters);
    },
    [updateWeight, fetchChartData, debouncedApplyFilters],
  );

  // Handle time period changes with date calculation and refetch
  const handleTimePeriodChange = useCallback(
    async (newTimePeriod: TimePeriod) => {
      // Update Redux state with new time period first
      updateTimePeriod(newTimePeriod);

      // Apply guardrail default rolling average for named periods (PRD Module 2)
      if (
        newTimePeriod === "latest_quarter" ||
        newTimePeriod === "previous_quarter"
      ) {
        const g = pitGuardrails?.units.quarter;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          // If no guardrails, disable rolling average
          updateRollingAverageEnabled(false);
          updateRollingAverageWindowSize(2);
        }
      } else if (
        newTimePeriod === "latest_semester" ||
        newTimePeriod === "previous_semester"
      ) {
        const g = pitGuardrails?.units.semester;
        if (g) {
          updateRollingAverageEnabled(g.defaultRollingAverage !== 0);
          updateRollingAverageWindowSize(
            g.defaultRollingAverage !== 0 ? g.defaultRollingAverage : 2,
          );
        } else {
          // If no guardrails, disable rolling average
          updateRollingAverageEnabled(false);
          updateRollingAverageWindowSize(2);
        }
      } else if (newTimePeriod === "all_time") {
        // All time: no rolling average, no stat testing
        updateRollingAverageEnabled(false);
        updateStatTestingEnabled(false);
        updateRollingAverageWindowSize(2);
      } else {
        // For custom_period and other periods, reset to default
        updateRollingAverageWindowSize(2);
      }

      // For custom_period, set default dates and don't fetch
      if (newTimePeriod === "custom_period") {
        // If responsesMinDate is available, use it for start; use dataAvailabilityDate for end if set
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          updateTimePeriodDates({
            startDate: currentConfig.responsesMinDate,
            endDate: dataAvailabilityCutoff || currentConfig.responsesMaxDate,
          });
          return;
        }

        // Otherwise, fetch data first to get the min/max dates
        if (datasetId && questionId) {
          try {
            const result = await getDatasetQuestionsResponse({
              id: questionId,
              datasetId: datasetId,
              topN: null,
              sorting: "ORIGINAL",
              weightSelection: currentConfig.weight,
              chartType: "TABLE",
              filters:
                debouncedApplyFilters && activeFilters.length > 0
                  ? activeFilters
                  : undefined,
            });

            if (
              result.data?.responsesMinDate &&
              result.data?.responsesMaxDate
            ) {
              updateTimePeriodDates({
                startDate: result.data.responsesMinDate,
                endDate: dataAvailabilityCutoff || result.data.responsesMaxDate,
              });

              // Update table data with the fetched data
              if (result.data?.data && Array.isArray(result.data.data)) {
                updateTableData({
                  data: result.data.data,
                  responsesMinDate: result.data.responsesMinDate,
                  responsesMaxDate: result.data.responsesMaxDate,
                });

                if (result.data?.weightsApplied !== undefined) {
                  setWeightsApplied(result.data.weightsApplied);
                }
              }
            }
          } catch (error) {
            console.error("Error fetching min/max dates:", error);
          }
        }
        return;
      }

      // Calculate dates based on the selected time period
      let dates: { startDate: string | null; endDate: string | null } = {
        startDate: null,
        endDate: null,
      };

      if (newTimePeriod === "all_time") {
        // All time: no dates, no rolling average
        updateRollingAverageEnabled(false);
      } else {
        const calculatedDates = getTimePeriodDates(
          newTimePeriod,
          dataAvailabilityCutoff,
        );
        if (calculatedDates) {
          dates = calculatedDates as any;
        }
      }

      // Update dates in Redux
      updateTimePeriodDates(dates);

      // Refetch data with the new dates
      if (datasetId && questionId) {
        console.log("📊 [handleTimePeriodChange] Fetching with dates", dates);

        const isAllTime = newTimePeriod === "all_time";
        const shouldIncludeDates =
          !isAllTime && dates.startDate && dates.endDate;

        try {
          const result = await getDatasetQuestionsResponse({
            id: questionId,
            datasetId: datasetId,
            topN: null,
            sorting: "ORIGINAL",
            weightSelection: currentConfig.weight,
            chartType: "TABLE",
            filters:
              debouncedApplyFilters && activeFilters.length > 0
                ? activeFilters
                : undefined,
            // Include date range only for non-all_time periods
            ...(shouldIncludeDates
              ? {
                  startDate: dates.startDate!,
                  endDate: dates.endDate!,
                }
              : {}),
            // Include statistical testing parameters if enabled (and not all_time)
            ...(currentConfig.statisticalTesting?.enabled && shouldIncludeDates
              ? {
                  enableStatTesting: true,
                  statTestingType: testTypeToApiFormat(
                    currentConfig.statisticalTesting?.testType,
                  ),
                  pValue: significanceLevelToPValue(
                    currentConfig.statisticalTesting?.significanceLevel ||
                      "90%",
                  ),
                  minimumSampleSize:
                    currentConfig.statisticalTesting?.minimumSampleSize || 30,
                }
              : {}),
            // No periodCount for all_time; include when rolling average is enabled.
            // Use 2 (the reset value) instead of the stale ref windowSize.
            ...(!isAllTime &&
              rollingAverageRef.current?.enabled && {
                periodCount: 2,
              }),
          });

          if (result.data?.data && Array.isArray(result.data.data)) {
            updateTableData({
              data: result.data.data,
              responsesMinDate: result.data.responsesMinDate,
              responsesMaxDate: result.data.responsesMaxDate,
              effectiveRollingPeriodCount:
                result.data?.effectiveRollingPeriodCount,
              rollingPeriodsWarningMessage:
                result.data?.rollingPeriodsWarningMessage,
              comparisonRollingLabel: result.data?.comparisonRollingLabel,
              missingRollingPeriodLabels:
                result.data?.missingRollingPeriodLabels,
            });

            if (result.data?.weightsApplied !== undefined) {
              setWeightsApplied(result.data.weightsApplied);
            }
          }
        } catch (error) {
          console.error("Error fetching chart data:", error);
        }
      }
    },
    [
      updateTimePeriod,
      updateTimePeriodDates,
      updateRollingAverageEnabled,
      updateRollingAverageWindowSize,
      pitGuardrails,
      datasetId,
      questionId,
      currentConfig.weight,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.statisticalTesting?.enabled,
      currentConfig.statisticalTesting?.testType,
      currentConfig.statisticalTesting?.significanceLevel,
      currentConfig.statisticalTesting?.minimumSampleSize,
      debouncedApplyFilters,
      activeFilters,
      getDatasetQuestionsResponse,
      updateTableData,
    ],
  );

  // Handle time period dates changes with refetch
  const handleTimePeriodDatesChange = useCallback(
    async (dates: { startDate: string | null; endDate: string | null }) => {
      console.log("🟣 [handleTimePeriodDatesChange] CALLED", {
        dates,
        currentDates: {
          startDate: currentConfig.timePeriodSelection?.startDate,
          endDate: currentConfig.timePeriodSelection?.endDate,
        },
        isHandlingStatTestingChange: isHandlingStatTestingChange.current,
      });

      // CRITICAL: Block if we're currently handling stat testing to prevent duplicate request
      if (isHandlingStatTestingChange.current) {
        console.log(
          "🔴 [handleTimePeriodDatesChange] BLOCKED - stat testing in progress",
        );
        updateTimePeriodDates(dates);
        return;
      }

      // Check if dates have actually changed to prevent unnecessary refetches
      const currentStartDate = currentConfig.timePeriodSelection?.startDate;
      const currentEndDate = currentConfig.timePeriodSelection?.endDate;

      const datesChanged =
        currentStartDate !== dates.startDate ||
        currentEndDate !== dates.endDate;

      console.log(
        "🟣 [handleTimePeriodDatesChange] datesChanged:",
        datesChanged,
      );

      // Validate date range BEFORE updating Redux - don't update or fetch if end date is before start date
      if (dates.startDate && dates.endDate) {
        const startDate = new Date(dates.startDate);
        const endDate = new Date(dates.endDate);

        if (endDate < startDate) {
          console.log(
            "🔴 [handleTimePeriodDatesChange] BLOCKED - end date is before start date",
            { startDate: dates.startDate, endDate: dates.endDate },
          );
          // Still update Redux so the error shows in UI, but don't fetch
          updateTimePeriodDates(dates);
          return;
        }
      }

      updateTimePeriodDates(dates);

      // Only refetch if dates actually changed AND both dates are set (valid complete range)
      if (datasetId && questionId && datesChanged) {
        if (dates.startDate && dates.endDate) {
          console.log(
            "📊 [handleTimePeriodDatesChange] REQUEST - Fetching with new dates",
            { startDate: dates.startDate, endDate: dates.endDate },
          );
          // Pass dates directly to avoid using stale Redux state
          await fetchChartData(
            currentConfig.weight,
            debouncedApplyFilters,
            dates,
          );
        } else if (!dates.startDate && !dates.endDate) {
          console.log(
            "📊 [handleTimePeriodDatesChange] REQUEST - Fetching without dates (all time)",
          );
          // Pass null dates explicitly
          await fetchChartData(currentConfig.weight, debouncedApplyFilters, {
            startDate: null,
            endDate: null,
          });
        } else {
          console.log(
            "🟡 [handleTimePeriodDatesChange] SKIPPED - incomplete dates",
            { startDate: dates.startDate, endDate: dates.endDate },
          );
        }
      } else {
        console.log(
          "🟡 [handleTimePeriodDatesChange] SKIPPED - no change or missing IDs",
        );
      }
    },
    [
      updateTimePeriodDates,
      datasetId,
      questionId,
      fetchChartData,
      currentConfig.weight,
      currentConfig.timePeriodSelection,
      debouncedApplyFilters,
    ],
  );

  // Handle statistical testing changes with refetch
  const handleStatTestingChange = useCallback(
    async (
      updateFn: () => void,
      newValues?: {
        enabled?: boolean;
        testType?: StatTestType;
        significanceLevel?: SignificanceLevel;
        minimumSampleSize?: number;
      },
    ) => {
      console.log("🔵 [handleStatTestingChange] CALLED", {
        newValues,
        isHandlingStatTestingChange: isHandlingStatTestingChange.current,
        isInitialLoad: isInitialLoad.current,
      });

      // Prevent multiple simultaneous requests
      if (isHandlingStatTestingChange.current) {
        console.log("🔴 [handleStatTestingChange] BLOCKED - already handling");
        return;
      }

      updateFn();

      // Skip refetch if we're in initial load (drawer just opened)
      if (isInitialLoad.current) {
        console.log("🟡 [handleStatTestingChange] SKIPPED - initial load");
        isInitialLoad.current = false;
        return;
      }

      // Set flag to prevent duplicate requests
      isHandlingStatTestingChange.current = true;
      console.log("🟢 [handleStatTestingChange] PROCEEDING with request");

      // Refetch data with new statistical testing settings
      if (datasetId && questionId) {
        // Determine if we're enabling or disabling
        const enabled =
          newValues?.enabled !== undefined
            ? newValues.enabled
            : currentConfig.statisticalTesting?.enabled || false;

        // If DISABLING statistical testing
        if (newValues?.enabled === false) {
          console.log(
            "📊 [handleStatTestingChange] DISABLING - Fetching WITHOUT stat testing",
          );

          // Refetch without stat testing but preserve current time period/dates
          try {
            const ra = rollingAverageRef.current;
            const rollingActive = ra?.enabled && ra?.windowSize;

            const result = await getDatasetQuestionsResponse({
              id: questionId,
              datasetId: datasetId,
              topN: null,
              sorting: "ORIGINAL" as const,
              weightSelection: currentConfig.weight,
              chartType: "TABLE" as const,
              filters:
                debouncedApplyFilters && activeFilters.length > 0
                  ? activeFilters
                  : undefined,
              // Preserve dates if rolling average is active
              ...(rollingActive &&
                currentConfig.timePeriodSelection?.startDate &&
                currentConfig.timePeriodSelection?.endDate && {
                  startDate: currentConfig.timePeriodSelection.startDate,
                  endDate: currentConfig.timePeriodSelection.endDate,
                }),
              // Include rolling average periodCount if still enabled
              ...(rollingActive && {
                periodCount: ra.windowSize,
              }),
            });

            if (result.data?.data && Array.isArray(result.data.data)) {
              updateTableData({
                data: result.data.data,
                responsesMinDate: result.data.responsesMinDate,
                responsesMaxDate: result.data.responsesMaxDate,
                effectiveRollingPeriodCount:
                  result.data?.effectiveRollingPeriodCount,
                rollingPeriodsWarningMessage:
                  result.data?.rollingPeriodsWarningMessage,
                comparisonRollingLabel: result.data?.comparisonRollingLabel,
                missingRollingPeriodLabels:
                  result.data?.missingRollingPeriodLabels,
              });

              if (result.data?.weightsApplied !== undefined) {
                setWeightsApplied(result.data.weightsApplied);
              }
            }
          } catch (error) {
            console.error("Error fetching chart data:", error);
          } finally {
            isHandlingStatTestingChange.current = false;
            console.log("✅ [handleStatTestingChange] COMPLETED - flag reset");
          }
          return;
        }

        // If ENABLING statistical testing
        let startDate: string | null | undefined =
          currentConfig.timePeriodSelection?.startDate;
        let endDate: string | null | undefined =
          currentConfig.timePeriodSelection?.endDate;

        const testType = newValues?.testType
          ? newValues.testType
          : currentConfig.statisticalTesting?.testType || "z-test";

        const significanceLevel = newValues?.significanceLevel
          ? newValues.significanceLevel
          : currentConfig.statisticalTesting?.significanceLevel || "90%";

        const minimumSampleSize =
          newValues?.minimumSampleSize !== undefined
            ? newValues.minimumSampleSize
            : currentConfig.statisticalTesting?.minimumSampleSize || 30;

        // If dates are null (all_time), use responsesMinDate/responsesMaxDate
        if (
          !startDate &&
          !endDate &&
          currentConfig.responsesMinDate &&
          currentConfig.responsesMaxDate
        ) {
          startDate = currentConfig.responsesMinDate;
          endDate = currentConfig.responsesMaxDate;
        }

        // If we still don't have dates, fetch them first
        if (!startDate && !endDate) {
          console.log(
            "📅 [handleStatTestingChange] REQUEST #1 - Fetching date range",
          );
          try {
            const dateResult = await getDatasetQuestionsResponse({
              id: questionId,
              datasetId: datasetId,
              topN: null,
              sorting: "ORIGINAL" as const,
              weightSelection: currentConfig.weight,
              chartType: "TABLE" as const,
              filters:
                debouncedApplyFilters && activeFilters.length > 0
                  ? activeFilters
                  : undefined,
            });

            if (
              dateResult.data?.responsesMinDate &&
              dateResult.data?.responsesMaxDate
            ) {
              startDate = dateResult.data.responsesMinDate;
              endDate = dateResult.data.responsesMaxDate;

              // Update timePeriodSelection with the fetched dates
              updateTimePeriodDates({
                startDate: startDate,
                endDate: endDate,
              });
            }
          } catch (error) {
            console.error("Error fetching date range:", error);
            isHandlingStatTestingChange.current = false;
            return;
          }
        }

        console.log(
          "📊 [handleStatTestingChange] REQUEST #2 - Fetching WITH stat testing",
          {
            startDate,
            endDate,
            significanceLevel,
            minimumSampleSize,
          },
        );

        try {
          const result = await getDatasetQuestionsResponse({
            id: questionId,
            datasetId: datasetId,
            topN: null,
            sorting: "ORIGINAL" as const,
            weightSelection: currentConfig.weight,
            chartType: "TABLE" as const,
            filters:
              debouncedApplyFilters && activeFilters.length > 0
                ? activeFilters
                : undefined,
            // Always include dates when stat testing is enabled
            startDate: startDate!,
            endDate: endDate!,
            // Include statistical testing parameters
            enableStatTesting: true,
            statTestingType: testTypeToApiFormat(testType),
            pValue: significanceLevelToPValue(significanceLevel),
            minimumSampleSize: minimumSampleSize,
            // Include rolling average periodCount when enabled
            ...(rollingAverageRef.current?.enabled &&
              rollingAverageRef.current?.windowSize && {
                periodCount: rollingAverageRef.current.windowSize,
              }),
          });

          if (result.data?.data && Array.isArray(result.data.data)) {
            updateTableData({
              data: result.data.data,
              responsesMinDate:
                result.data.responsesMinDate || startDate || undefined,
              responsesMaxDate:
                result.data.responsesMaxDate || endDate || undefined,
              effectiveRollingPeriodCount:
                result.data?.effectiveRollingPeriodCount,
              rollingPeriodsWarningMessage:
                result.data?.rollingPeriodsWarningMessage,
              comparisonRollingLabel: result.data?.comparisonRollingLabel,
              missingRollingPeriodLabels:
                result.data?.missingRollingPeriodLabels,
            });

            // Ensure timePeriodSelection is updated with the dates used in the request
            if (startDate && endDate) {
              updateTimePeriodDates({
                startDate: startDate,
                endDate: endDate,
              });
            }

            if (result.data?.weightsApplied !== undefined) {
              setWeightsApplied(result.data.weightsApplied);
            }
          }
        } catch (error) {
          console.error("Error fetching chart data:", error);
        } finally {
          isHandlingStatTestingChange.current = false;
          console.log("✅ [handleStatTestingChange] COMPLETED - flag reset");
        }
      }
    },
    [
      datasetId,
      questionId,
      currentConfig.weight,
      currentConfig.timePeriodSelection,
      currentConfig.statisticalTesting,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      debouncedApplyFilters,
      activeFilters,
      getDatasetQuestionsResponse,
      updateTableData,
      updateTimePeriodDates,
      updateTimePeriod,
    ],
  );

  const handleSave = async () => {
    // Store the current preview state
    const wasShowingFiltered = localApplyFilters;

    // Step 1: Fetch unfiltered data ONLY if filters were applied in preview
    // If no filters were applied, we already have the original unfiltered data
    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      await fetchChartData(currentConfig.weight, false); // false = no filters
    }

    if (pendingMemoryEntry) {
      questionSessionMemory.push(pendingMemoryEntry.dashboardId, {
        questionId: pendingMemoryEntry.questionId,
        questionType: pendingMemoryEntry.questionType,
        datasetId: pendingMemoryEntry.datasetId,
        slot: "general",
      });
    }

    // Step 2: Save config to Redux (triggers useTableConfigSave hook)
    saveCurrentConfig();

    // Step 3: Close drawer after a brief delay to allow widget save to complete
    // The useTableConfigSave hook needs editingWidgetId to still be set to detect the save
    setTimeout(() => {
      closeChartDrawer();
    }, 50);

    // Step 4: Apply filters in background after drawer closes and widget save completes
    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      setTimeout(() => {
        fetchChartData(currentConfig.weight, true); // true = apply filters
      }, 100);
    }
  };

  const handleDiscard = () => {
    resetToDefault();
    closeChartDrawer();
  };

  const isAvailableData = currentConfig.data && currentConfig.data.length > 0;

  // Check if statistical testing is enabled but no previous period data exists
  // Table data has statSignificance in descriptiveMetrics array
  const hasNoPreviousPeriodData =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.data.length > 0 &&
    currentConfig.data.every((row) =>
      row.surveys.every((survey) => {
        const meanMetric = (survey as any).descriptiveMetrics?.find(
          (m: any) => m.valueMetric === "mean",
        );
        return (
          meanMetric?.statSignificance === undefined ||
          meanMetric?.statSignificance === null
        );
      }),
    );

  // Check if sample size is below threshold
  const hasSampleSizeBelowThreshold =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.data.length > 0 &&
    currentConfig.data.some((row) =>
      row.surveys.some((survey) =>
        survey.descriptiveMetrics?.some(
          (metric) =>
            metric.baseN !== undefined &&
            metric.baseN < currentConfig.statisticalTesting.minimumSampleSize,
        ),
      ),
    );

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="flex-1">
            <Typography className="font-bold" variant="h6">
              {questions?.label || "Table Configuration"}
            </Typography>
            <Typography variant="body1">
              {(() => {
                if (!questions?.name) {
                  return "Configure your table";
                }

                const baseTitle = questions.name;

                const rollingSuffix =
                  currentConfig.rollingAverage?.enabled && currentConfig.rollingAverage?.windowSize
                    ? getRollingAverageTitleSuffix(currentConfig.rollingAverage.windowSize)
                    : "";

                // Check if user has customized the title
                const hasCustomTitle =
                  currentConfig.title !== "" &&
                  questions &&
                  currentConfig.title !== questions.name;

                // If title is customized, show the custom title
                if (hasCustomTitle) {
                  return currentConfig.title;
                }

                // If title is NOT customized and time period is set, append it
                if (timePeriodSelection) {
                  const timePeriodSuffix = getTimePeriodTitleSuffix(
                    timePeriodSelection.timePeriod,
                    timePeriodSelection.startDate,
                    timePeriodSelection.endDate,
                  );

                  if (timePeriodSuffix) {
                    return `${baseTitle}${timePeriodSuffix}${rollingSuffix}`;
                  }
                }

                return `${baseTitle}${rollingSuffix}`;
              })()}
            </Typography>
            {datasetName && (
              <Typography variant="caption" color="text.secondary">
                Dataset: {datasetName}
              </Typography>
            )}
          </div>
          {(() => {
            const hasDashboardDateFilters = !!(
              new URLSearchParams(window.location.search).get("df_start") &&
              new URLSearchParams(window.location.search).get("df_end")
            );
            return (
              <>
                <DrawerFilterToggles
                  hasDashboardDateFilters={hasDashboardDateFilters}
                  usePreviewDateFilter={usePreviewDateFilter}
                  onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
                  activeFiltersCount={activeFilters.length}
                  localApplyFilters={localApplyFilters}
                  onFilterToggle={handleFilterToggle}
                  disabled={isTogglingFilters}
                />
              </>
            );
          })()}
        </Box>

        {/* Main Content - Side by Side Layout */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Rounding
                    </FormLabel>
                    <Select
                      value={currentConfig.percentageFormatting}
                      onChange={(e) =>
                        updatePercentageFormatting(e.target.value as number)
                      }
                      disabled={isRefetchingData}
                    >
                      <MenuItem value={0}>0 decimals</MenuItem>
                      <MenuItem value={1}>1 decimal</MenuItem>
                      <MenuItem value={2}>2 decimals</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Weight
                    </FormLabel>
                    <Select
                      value={currentConfig.weight}
                      onChange={(e) => {
                        handleWeightChange(
                          e.target.value as
                            | "DISABLED"
                            | "DATASET_DEFAULT"
                            | "SURVEY_WEIGHT",
                        );
                      }}
                      disabled={isRefetchingData}
                    >
                      <MenuItem value="DISABLED">Disabled</MenuItem>
                      <MenuItem value="DATASET_DEFAULT">
                        Use Default Weight
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {currentConfig.weight === "DISABLED" && (
                    <Alert
                      severity="warning"
                      icon={<WarningIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        "& .MuiAlert-message": { fontSize: "0.75rem" },
                      }}
                    >
                      Weights disabled: unweighted counts shown.
                    </Alert>
                  )}

                  {currentConfig.weight === "DATASET_DEFAULT" &&
                    !weightsApplied && (
                      <Alert
                        severity="warning"
                        icon={<WarningIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          "& .MuiAlert-message": { fontSize: "0.75rem" },
                        }}
                      >
                        No default weight configured for this dataset. Showing
                        unweighted counts.
                      </Alert>
                    )}
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Row sorting
                    </FormLabel>
                    <Select
                      value={currentConfig.dataSorting}
                      onChange={(e) => {
                        const newValue = e.target.value as
                          | "ascending"
                          | "descending"
                          | "none"
                          | "manual";
                        updateDataSorting(newValue);

                        // Reset row order to original data order when switching to manual or none
                        if (newValue === "manual" || newValue === "none") {
                          resetRowOrder();
                        }
                      }}
                      disabled={isRefetchingData}
                    >
                      <MenuItem value="none">Keep original order</MenuItem>
                      <MenuItem value="ascending">
                        Sort by Mean (ascending)
                      </MenuItem>
                      <MenuItem value="descending">
                        Sort by Mean (descending)
                      </MenuItem>
                      <MenuItem value="manual">Manual Order</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Table Headers
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <HPInput
                    label="Row header label"
                    value={currentConfig.rowHeaderLabel}
                    onChange={(e) => updateRowHeaderLabel(e.target.value)}
                    placeholder="e.g., RQ Dimensions, Metrics"
                    disabled={isRefetchingData}
                  />
                  <HPInput
                    label="Column header label"
                    value={currentConfig.columnHeaderLabel}
                    onChange={(e) => updateColumnHeaderLabel(e.target.value)}
                    placeholder="e.g., Mean, Score"
                    disabled={isRefetchingData}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Time Period & Rolling Average */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Time Period
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TimePeriodSelection
                    timePeriodSelection={currentConfig.timePeriodSelection}
                    onTimePeriodChange={handleTimePeriodChange}
                    onTimePeriodDatesChange={handleTimePeriodDatesChange}
                    statisticalTestingEnabled={
                      currentConfig.statisticalTesting?.enabled || false
                    }
                    responsesMinDate={currentConfig.responsesMinDate}
                    responsesMaxDate={currentConfig.responsesMaxDate}
                  />

                  <RollingAverageSection
                    enabled={currentConfig.rollingAverage?.enabled || false}
                    windowSize={currentConfig.rollingAverage?.windowSize || 2}
                    timePeriod={
                      currentConfig.timePeriodSelection?.timePeriod ||
                      "all_time"
                    }
                    guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                    guardrailRollingDisabled={isRollingAverageDisabledByPolicy}
                    guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                    guardrailConflictWarning={pitGuardrailConflict ?? undefined}
                    disabled={isRefetchingData}
                    onEnabledChange={(enabled) => {
                      updateRollingAverageEnabled(enabled);
                      // Pass current dates explicitly so they're included when rolling average needs them
                      const dates =
                        currentConfig.timePeriodSelection?.startDate &&
                        currentConfig.timePeriodSelection?.endDate
                          ? {
                              startDate:
                                currentConfig.timePeriodSelection.startDate,
                              endDate:
                                currentConfig.timePeriodSelection.endDate,
                            }
                          : currentConfig.responsesMinDate &&
                              currentConfig.responsesMaxDate
                            ? {
                                startDate: currentConfig.responsesMinDate,
                                endDate: currentConfig.responsesMaxDate,
                              }
                            : undefined;
                      fetchChartData(
                        currentConfig.weight,
                        debouncedApplyFilters,
                        dates,
                        enabled
                          ? currentConfig.rollingAverage?.windowSize || 2
                          : null,
                      );
                    }}
                    onWindowSizeChange={(size) => {
                      updateRollingAverageWindowSize(size);
                      // Pass current dates explicitly so they're included when rolling average needs them
                      const dates =
                        currentConfig.timePeriodSelection?.startDate &&
                        currentConfig.timePeriodSelection?.endDate
                          ? {
                              startDate:
                                currentConfig.timePeriodSelection.startDate,
                              endDate:
                                currentConfig.timePeriodSelection.endDate,
                            }
                          : currentConfig.responsesMinDate &&
                              currentConfig.responsesMaxDate
                            ? {
                                startDate: currentConfig.responsesMinDate,
                                endDate: currentConfig.responsesMaxDate,
                              }
                            : undefined;
                      fetchChartData(
                        currentConfig.weight,
                        debouncedApplyFilters,
                        dates,
                        size,
                      );
                    }}
                  />

                  <PointInTimeViewerOptionsSection
                    viewerOptions={currentConfig.viewerOptions}
                    chartTimePeriod={currentPitPeriod}
                    chartRollingWindowSize={
                      currentConfig.rollingAverage?.enabled
                        ? (currentConfig.rollingAverage?.windowSize ?? 0)
                        : 0
                    }
                    pitGuardrailUnits={pitGuardrails.units}
                    isDisabled={isRefetchingData}
                    onViewerOptionsChange={(opts) =>
                      dispatch(updateViewerOptions(opts))
                    }
                  />
                </Box>
              </Box>

              <Divider />

              {/* Statistical Testing */}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Statistical Testing
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          currentConfig.statisticalTesting?.enabled || false
                        }
                        onChange={(e) =>
                          handleStatTestingChange(
                            () => updateStatTestingEnabled(e.target.checked),
                            { enabled: e.target.checked },
                          )
                        }
                        size="small"
                        disabled={isRefetchingData || isAllTimePeriod}
                      />
                    }
                    label="Enable Statistical Testing"
                  />

                  {currentConfig.statisticalTesting?.enabled && (
                    <>
                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Test Type
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting?.testType ||
                            "z-test"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateStatTestType(
                                  e.target.value as StatTestType,
                                ),
                              { testType: e.target.value as StatTestType },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="z-test">
                            Z-Test (Proportions)
                          </MenuItem>
                          <MenuItem value="t-test">
                            T-Test (with Overlap)
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Significance Level
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting
                              ?.significanceLevel || "95%"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateSignificanceLevel(
                                  e.target.value as SignificanceLevel,
                                ),
                              {
                                significanceLevel: e.target
                                  .value as SignificanceLevel,
                              },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="90%">90% Confidence</MenuItem>
                          <MenuItem value="95%">95% Confidence</MenuItem>
                          <MenuItem value="99%">99% Confidence</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626] mb-1">
                          Minimum Sample Size
                        </FormLabel>
                        <TextField
                          disabled={isRefetchingData}
                          type="text"
                          value={String(
                            currentConfig.statisticalTesting
                              ?.minimumSampleSize || 30,
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers (no letters, signs, or special characters)
                            if (value === "" || /^\d+$/.test(value)) {
                              const newSize = value === "" ? 0 : Number(value);
                              handleStatTestingChange(
                                () => updateMinimumSampleSize(newSize),
                                { minimumSampleSize: newSize },
                              );
                            }
                          }}
                          size="small"
                          fullWidth
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                              {
                                display: "none",
                              },
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                          }}
                        />
                        <FormHelperText>
                          Minimum sample size required for statistical testing
                        </FormHelperText>
                      </FormControl>

                      {/* <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                currentConfig.statisticalTesting
                                  ?.compareToTotal || false
                              }
                              onChange={(e) =>
                                handleStatTestingChange(() =>
                                  updateCompareToTotal(e.target.checked)
                                )
                              }
                              size="small"
                            />
                          }
                          label="Compare to Total Column"
                        /> */}

                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f5f5f5",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: "block", mb: 1 }}
                        >
                          Statistical Markers Legend:
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block" }}>
                          <strong>▲</strong> Significantly higher than previous
                          period
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block" }}>
                          <strong>▼</strong> Significantly lower than previous
                          period
                        </Typography>
                      </Box>
                    </>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Statistical testing helps identify significant differences
                    in your data
                  </Typography>
                </Box>
              </Box>
              <Divider />

              {/* Styling & Customization */}
              <Accordion
                defaultExpanded
                sx={{ boxShadow: "none", border: "1px solid #eee" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Styling & Customization
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ position: "relative" }}>
                          <HPInput
                            label="Widget title"
                            value={currentConfig.title}
                            onChange={(e) => updateChartTitle(e.target.value)}
                            disabled={isRefetchingData}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: "70%",
                              right: 0,
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <ToggleButton
                              value="bold"
                              selected={currentConfig.titleBold}
                              onChange={() =>
                                updateTitleBold(!currentConfig.titleBold)
                              }
                              size="small"
                              disabled={isRefetchingData}
                              sx={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                border: "1px solid #e0e0e0",
                                "&.Mui-selected": {
                                  backgroundColor: "#1976d2",
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: "#1565c0",
                                  },
                                },
                              }}
                            >
                              <FormatBoldIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton
                              value="underline"
                              selected={currentConfig.titleUnderline}
                              onChange={() =>
                                updateTitleUnderline(
                                  !currentConfig.titleUnderline,
                                )
                              }
                              size="small"
                              disabled={isRefetchingData}
                              sx={{
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                border: "1px solid #e0e0e0",
                                "&.Mui-selected": {
                                  backgroundColor: "#1976d2",
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: "#1565c0",
                                  },
                                },
                              }}
                            >
                              <FormatUnderlinedIcon fontSize="small" />
                            </ToggleButton>
                          </Box>
                        </Box>

                        <HPInput
                          label="Widget subtitle"
                          value={currentConfig.subtitle}
                          onChange={(e) => updateChartSubtitle(e.target.value)}
                          placeholder="Optional (e.g., Score 0-100)"
                          disabled={isRefetchingData}
                        />
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Divider />

              {/* Row Visibility */}
              <Accordion
                defaultExpanded
                sx={{ boxShadow: "none", border: "1px solid #eee" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Row Visibility
                      </Typography>
                      <Chip
                        label={`${currentConfig.data.filter((r) => !currentConfig.hiddenRows.includes(r.variableId)).length}/${currentConfig.data.length} visible`}
                        size="small"
                        color={
                          currentConfig.hiddenRows.length === 0
                            ? "default"
                            : "primary"
                        }
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <HPButton
                        variant="outlined"
                        size="small"
                        onClick={() => showAllRows()}
                        disabled={
                          isRefetchingData ||
                          currentConfig.hiddenRows.length === 0
                        }
                      >
                        Show All
                      </HPButton>
                      <HPButton
                        variant="outlined"
                        size="small"
                        onClick={() => hideAllRows()}
                        disabled={
                          isRefetchingData ||
                          currentConfig.hiddenRows.length ===
                            currentConfig.data.length
                        }
                      >
                        Hide All
                      </HPButton>
                    </Box>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <Box
                        sx={{
                          maxHeight: 400,
                          overflow: "auto",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {currentConfig.data.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2, textAlign: "center" }}
                          >
                            No rows available
                          </Typography>
                        ) : (
                          <SortableContext
                            items={currentConfig.rowOrder}
                            strategy={verticalListSortingStrategy}
                          >
                            {currentConfig.rowOrder.map((variableId) => {
                              const row = currentConfig.data.find(
                                (r) => r.variableId === variableId,
                              );
                              if (!row) return null;

                              const isHidden =
                                currentConfig.hiddenRows.includes(variableId);
                              const hasCustomLabel =
                                !!currentConfig.customRowLabels[variableId];
                              const displayLabel =
                                currentConfig.customRowLabels[variableId] ||
                                row.variableLabel;
                              const isEditing = editingRowId === variableId;

                              return (
                                <SortableRowItem
                                  key={variableId}
                                  row={row}
                                  isHidden={isHidden}
                                  hasCustomLabel={hasCustomLabel}
                                  displayLabel={displayLabel}
                                  isEditing={isEditing}
                                  editValue={editRowValue}
                                  onToggleVisibility={toggleRowVisibility}
                                  onStartEdit={handleStartEditRow}
                                  onSaveEdit={handleSaveRowEdit}
                                  onCancelEdit={handleCancelRowEdit}
                                  onResetLabel={handleResetRowLabel}
                                  onEditValueChange={setEditRowValue}
                                  isDisabled={isRefetchingData}
                                  isDragDisabled={
                                    currentConfig.dataSorting !== "manual"
                                  }
                                />
                              );
                            })}
                          </SortableContext>
                        )}
                      </Box>
                    </DndContext>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>

          {/* Preview Panel */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Box
              sx={{
                flex: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                minHeight: 400,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                p: 2,
              }}
            >
              {isRefetchingData ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <LoadingSpinner message="Loading data..." />
                </Box>
              ) : isNoPeriodAvailable ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <EmptyChartState message={noPeriodMessage!} />
                </Box>
              ) : !isAvailableData ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                  }}
                >
                  <EmptyChartState message="No table data available" />
                </Box>
              ) : (
                <TableChart
                  config={{
                    data: currentConfig.data,
                    question: questions,
                    questionId: questionId || undefined,
                    dataSorting: currentConfig.dataSorting,
                    percentageFormatting: currentConfig.percentageFormatting,
                    rowHeaderLabel: currentConfig.rowHeaderLabel,
                    columnHeaderLabel: currentConfig.columnHeaderLabel,
                    hiddenRows: currentConfig.hiddenRows,
                    rowOrder: currentConfig.rowOrder,
                    customRowLabels: currentConfig.customRowLabels,
                    statisticalTesting: currentConfig.statisticalTesting,
                    timePeriodSelection: currentConfig.timePeriodSelection,
                    weight: currentConfig.weight,
                    rollingAverage: currentConfig.rollingAverage,
                    effectiveRollingPeriodCount:
                      currentConfig.effectiveRollingPeriodCount,
                    missingRollingPeriodLabels:
                      currentConfig.missingRollingPeriodLabels,
                    rollingPeriodsWarningMessage:
                      currentConfig.rollingPeriodsWarningMessage,
                    comparisonRollingLabel:
                      currentConfig.comparisonRollingLabel,
                  }}
                  hasCustomTitle={
                    currentConfig.title !== "" &&
                    questions &&
                    currentConfig.title !== questions.name
                  }
                  weightsApplied={weightsApplied}
                  dashboardDateOverride={dashboardDateOverride}
                />
              )}
            </Box>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {hasNoPreviousPeriodData && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  No data available for the previous time period to perform
                  statistical testing.
                </Alert>
              )}

              {!isRefetchingData &&
                isAvailableData &&
                currentConfig.rollingAverage?.enabled &&
                currentConfig.rollingAverage?.windowSize !== undefined &&
                currentConfig.effectiveRollingPeriodCount !== undefined &&
                currentConfig.rollingAverage.windowSize >
                  currentConfig.effectiveRollingPeriodCount && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Warning: Insufficient historical data for selected rolling
                    window. Available periods:{" "}
                    {currentConfig.effectiveRollingPeriodCount}. Rolling window
                    reduced to {currentConfig.effectiveRollingPeriodCount}{" "}
                    periods.
                  </Alert>
                )}

              {hasSampleSizeBelowThreshold && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {currentConfig.rollingAverage?.enabled
                    ? "Warning: Weighted base for one or more rolling windows is below the recommended threshold for reliable statistical testing."
                    : "Warning: Sample size for one or more time periods is below the recommended threshold for reliable statistical testing."}
                </Alert>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Source: {questions?.name || "Sample Data"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <HPButton variant="text" onClick={handleDiscard}>
            Discard
          </HPButton>
          <HPButton
            variant="contained"
            onClick={handleSave}
            disabled={isRefetchingData || isViewerOptionsInvalid || !!pitGuardrailConflict}
          >
            {isCreatingChart ? "Save" : "Update"}
          </HPButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TableChartConfigDrawer;
